import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from "../../components/Common/Breadcrumb";
import Select from "react-select";
import RolePermissionCard from "../../components/Common/RolePermissionCard";
import { addUser } from "../../services/userService";
import { fetchAllDepartments } from "../../services/departmentService";
import { message } from "antd";
import { label } from "yet-another-react-lightbox";
import { jwtDecode } from "jwt-decode";
import { fetchUserById } from "../../services/userService";

const AddUsers = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptOptions, setDeptOptions] = useState([]);
  const [isFaculty, setIsFaculty] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const [loggedInUserData, setLoggedInUserData] = useState(null);

  const roleOptions = [
    { value: null, label: "None" },
    { value: 1, label: "Admin" },
    { value: 2, label: "Department Co-ordinator" },
  ];

  useEffect(() => {
    const getDepartments = async () => {
      try {
        const data = await fetchAllDepartments();
        const formatted = data.map((dept) => ({
          value: dept.department_id,
          label: dept.department_name,
        }));

        // Prepend "None" option
        setDeptOptions([{ value: null, label: "None" }, ...formatted]);
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };

    getDepartments();
  }, []);

  useEffect(() => {
    const fetchAndSetUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const user = await fetchUserById(decoded.id);
          setLoggedInUserData(user);
        } catch (error) {
          console.error("Error decoding token or fetching user:", error);
          localStorage.removeItem("token");
          setLoggedInUserData(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchAndSetUser();
  }, []);

  const resetForm = () => {
    setName("");
    setEmail("");
    setContact("");
    setIsFaculty(false);
    setSelectedRole(null);
    setSelectedDept(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      let departmentId = selectedDept?.value;
      let role = selectedRole?.value;

      // If user is Dept Co-ordinator, auto-fill department and lock role
      if (loggedInUserData?.user_role === "2") {
        departmentId = loggedInUserData.department_id;
        role = null;
      }

      const parameter = loggedInUserData?.user_role === "1" ? "Admin" : "Department";

      const payload = {
        name,
        email,
        contact,
        isFaculty: loggedInUserData?.user_role === "2" ? true : isFaculty,
        role,
        departmentId,
        parameter,
        addedbyemail : loggedInUserData.user_email
      };

      await addUser(payload);
      message.success("User registered. Temporary password sent to email.");
      resetForm();
    } catch (err) {
      message.error(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: "User", path: "/list-users" },
    { label: "Add User" },
  ];

  return (
    <>
      <Helmet>
        <title>Add User | CRM Jnec</title>
      </Helmet>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={breadcrumbItems} />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-8">
                        <div className="p-3 border rounded bg-white">
                          <div className="row align-items-center mb-3">
                            {/* ✅ Hide checkbox for faculty if user role is 2 */}
                            {loggedInUserData?.user_role !== "2" && (
                              <div className="col-12 d-flex align-items-end">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="isFaculty"
                                    checked={isFaculty}
                                    onChange={(e) =>
                                      setIsFaculty(e.target.checked)
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="isFaculty"
                                  >
                                    Is Faculty
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* ✅ Name */}
                          <div className="mt-4 col-12">
                            <div className="form-group local-forms">
                              <label>
                                Name <span className="login-danger">*</span>
                              </label>
                              <input
                                className="form-control"
                                type="text"
                                placeholder="Enter User Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          {/* ✅ Email */}
                          <div className="col-12">
                            <div className="form-group local-forms">
                              <label>
                                Email <span className="login-danger">*</span>
                              </label>
                              <input
                                className="form-control"
                                type="email"
                                placeholder="Enter User Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          {/* ✅ Contact */}
                          <div className="col-12">
                            <div className="form-group local-forms">
                              <label>
                                Contact <span className="login-danger">*</span>
                              </label>
                              <input
                                className="form-control"
                                type="text"
                                placeholder="Enter User Contact Number"
                                value={contact}
                                onChange={(e) => setContact(e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          {/* ✅ Department — show or auto-fill */}
                          {loggedInUserData?.user_role !== "2" ? (
                            <div className="col-12">
                              <div className="form-group local-forms">
                                <label>
                                  Select Department (Type to search and select)
                                  <span className="login-danger">*</span>
                                </label>
                                <Select
                                  value={selectedDept}
                                  onChange={setSelectedDept}
                                  options={deptOptions}
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  styles={{
                                    control: (base, state) => ({
                                      ...base,
                                      borderColor: state.isFocused
                                        ? "#2e37a4"
                                        : "rgba(46, 55, 164, 0.1)",
                                      boxShadow: "none",
                                      borderRadius: "10px",
                                      fontSize: "14px",
                                      minHeight: "45px",
                                    }),
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="col-12">
                              <div className="form-group local-forms">
                                <label>Department</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={
                                    deptOptions.find(
                                      (d) =>
                                        d.value ===
                                        loggedInUserData.department_id
                                    )?.label || "Department not found"
                                  }
                                  disabled
                                />
                              </div>
                            </div>
                          )}

                          {/* ✅ Role — hide if user_role = 2 */}
                          {loggedInUserData?.user_role !== "2" && (
                            <div className="col-12">
                              <div className="form-group local-forms">
                                <label>
                                  Select Roles (Type to search and select)
                                  <span className="login-danger">*</span>
                                </label>
                                <Select
                                  value={selectedRole}
                                  onChange={setSelectedRole}
                                  options={roleOptions}
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  styles={{
                                    control: (base, state) => ({
                                      ...base,
                                      borderColor: state.isFocused
                                        ? "#2e37a4"
                                        : "rgba(46, 55, 164, 0.1)",
                                      boxShadow: "none",
                                      borderRadius: "10px",
                                      fontSize: "14px",
                                      minHeight: "45px",
                                    }),
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {/* ✅ Submit */}
                          <div className="text-end">
                            <button
                              type="submit"
                              className="btn btn-warning me-2 text-white"
                              disabled={loading}
                            >
                              {loading ? "Submitting..." : "Submit"}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Permissions Info */}
                      <div className="col-md-4 text-brown">
                        <div className="p-2 border rounded bg-light small">
                          <p
                            className="mb-2 fw-semibold"
                            style={{ fontSize: "14px" }}
                          >
                            <strong>Roles and permissions</strong>
                          </p>

                          <RolePermissionCard
                            role="Admin"
                            permissions={[
                              "manage members",
                              "manage events",
                              "manage news",
                              "manage notifications",
                              "manage departments",
                              "manage blogs",
                              "manage carousels",
                              "view statistics",
                            ]}
                          />
                          <RolePermissionCard
                            role="Department Co-ordinator"
                            permissions={[
                              "manage faculty",
                              "management module",
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddUsers;
